/**
 * Deslizar un mensaje hacia la derecha para citarlo, como en WhatsApp.
 *
 * No nació de copiar una app: nació de un dedo. Probando el chat en el móvil,
 * alguien quiso preguntar por una de las tres opciones en pantalla e hizo el
 * gesto sin pensarlo. No pasó nada. Ese "no pasó nada" es la señal de diseño más
 * confiable que existe — la mano ya sabía.
 *
 * Y resuelve algo que no es cosmético. Con tres tarjetas publicadas, «¿ese tiene
 * alberca?» obliga al bot a deducir de cuál habla. Acierta seguido, y cuando
 * falla contesta con seguridad sobre el hotel equivocado: el peor error, porque
 * suena bien y nadie lo detecta hasta que el cliente llega.
 *
 * Este módulo es solo la mecánica del gesto —sin Vue, sin DOM propio— para
 * poder probar los umbrales sin un navegador.
 */

/** Cuánto hay que arrastrar para que la cita se tome. */
export const UMBRAL_PX = 56

/** Hasta dónde se deja llegar al dedo. Más allá, el mensaje ya no se mueve. */
export const TOPE_PX = 78

/**
 * A partir de qué desvío se considera que el gesto es vertical.
 *
 * Sin esto, un desplazamiento normal de la conversación arrastraría los
 * mensajes de lado en el camino. El dedo casi nunca sube recto.
 */
const TOLERANCIA_VERTICAL = 1.2

export interface Arrastre {
  /** Cuánto se ha movido en horizontal, ya con la resistencia aplicada. */
  dx: number
  /** ¿Se soltó ya pasado el umbral? Es lo que decide si se cita. */
  cita: boolean
  /** ¿El gesto sigue vivo? Cuando es falso, el llamador deja de escuchar. */
  activo: boolean
}

/**
 * La resistencia del final del recorrido.
 *
 * Sin ella el mensaje se despega del dedo al llegar al tope y se siente roto.
 * Con ella sigue respondiendo, cada vez menos — que es lo que hace que un
 * gesto se sienta físico en vez de programado.
 */
function conResistencia(dx: number): number {
  if (dx <= UMBRAL_PX) return dx
  const exceso = dx - UMBRAL_PX
  return Math.min(UMBRAL_PX + exceso * 0.35, TOPE_PX)
}

/**
 * Sigue un gesto desde que el dedo toca hasta que se levanta.
 *
 * Se usa como máquina: `iniciar` en el touchstart, `mover` en cada touchmove,
 * `soltar` al final. Devuelve el estado en vez de tocar el DOM para que la capa
 * de Vue decida qué pintar.
 */
export class GestoDeCita {
  private x0 = 0
  private y0 = 0
  private vivo = false
  /** Una vez que se decide que el gesto es vertical, ya no se recupera. */
  private descartado = false
  /** El último desplazamiento crudo, para saber al soltar si pasó el umbral. */
  private ultimoDx = 0

  iniciar(x: number, y: number): void {
    this.x0 = x
    this.y0 = y
    this.vivo = true
    this.descartado = false
    this.ultimoDx = 0
  }

  /**
   * Devuelve `null` cuando el gesto no le pertenece —es un desplazamiento
   * vertical, o va hacia la izquierda— y el llamador debe dejar pasar el evento
   * sin cancelarlo. Cancelar de más rompe el scroll de la conversación.
   */
  mover(x: number, y: number): Arrastre | null {
    if (!this.vivo || this.descartado) return null
    const dx = x - this.x0
    const dy = Math.abs(y - this.y0)

    // Hacia la izquierda no hay nada que citar; se ignora sin descartar el
    // gesto, por si el dedo vuelve —la mano tiembla y el recorrido no es recto.
    if (dx <= 0) {
      this.ultimoDx = 0
      return { dx: 0, cita: false, activo: true }
    }

    // Vertical gana: es la conversación desplazándose, no una cita.
    if (dy > dx * TOLERANCIA_VERTICAL) {
      this.descartado = true
      return null
    }
    this.ultimoDx = dx
    return { dx: conResistencia(dx), cita: dx >= UMBRAL_PX, activo: true }
  }

  /**
   * Cierra el gesto y dice si hay que citar.
   *
   * El mensaje vuelve siempre a su sitio (`dx: 0`): la cita se confirma con el
   * chip que aparece sobre el teclado, no dejando la burbuja corrida. Un
   * elemento que se queda fuera de lugar se lee como un error de la interfaz.
   */
  soltar(): Arrastre {
    const cita = this.vivo && !this.descartado && this.ultimoDx >= UMBRAL_PX
    this.vivo = false
    this.ultimoDx = 0
    return { dx: 0, cita, activo: false }
  }

  /** ¿El gesto sigue siendo candidato a cita? */
  get activo(): boolean {
    return this.vivo && !this.descartado
  }
}
